import { Component } from "@theme/component";
import { CartAddEvent, ToastEvent } from "@theme/events";
function randomTeamId() {
  const alphabet = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
  const bytes = crypto.getRandomValues(new Uint8Array(12));
  let id = "";
  for (const byte of bytes) id += alphabet[byte % 62];
  return id;
}
class ParticipantRegistrationForm extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("click", this.#onClick);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("click", this.#onClick);
  }
  get #dialog() {
    return this.querySelector("dialog-component");
  }
  #onClick = (event) => {
    const target = event.target;
    if (!target?.closest("[data-register-trigger]")) return;
    this.#syncParticipants(this.#teamSize());
    this.#dialog?.showDialog?.();
  };
  // Team size is fixed on the event (no quantity counter).
  #teamSize() {
    const value = Number.parseInt(this.dataset.teamSize ?? "1", 10);
    return Number.isFinite(value) && value >= 1 ? value : 1;
  }
  // Team vs individual is derived solely from team size: > 1 means the buyer
  // registers a team of that many participants.
  #isTeam() {
    return this.#teamSize() > 1;
  }
  #syncParticipants(count) {
    const container = this.querySelector("[data-participants]");
    const template = this.querySelector("template");
    if (!container || !template) return;
    const current = container.querySelectorAll("details").length;
    if (count === current) return;
    if (count > current) {
      for (let i = current; i < count; i++) {
        container.append(this.#cloneParticipant(template, i));
      }
    } else {
      const details = container.querySelectorAll("details");
      for (let i = current - 1; i >= count; i--) {
        details[i]?.remove();
      }
    }
  }
  #cloneParticipant(template, index) {
    const fragment = template.content.cloneNode(true);
    const details = fragment.querySelector("details");
    if (!details) return fragment.firstElementChild;
    const suffix = `-${index}`;
    const number = details.querySelector(
      "[data-participant-number]"
    );
    if (number) number.textContent = String(index + 1);
    for (const el of details.querySelectorAll("[id]")) {
      el.id = `${el.id}${suffix}`;
    }
    for (const el of details.querySelectorAll(
      "label[for], [aria-describedby]"
    )) {
      for (const attr of ["for", "aria-describedby"]) {
        const value = el.getAttribute(attr);
        if (value) el.setAttribute(attr, `${value}${suffix}`);
      }
    }
    return details;
  }
  handleSubmit(event) {
    event.preventDefault();
    void this.#submit();
  }
  async #submit() {
    if (this.dataset.submitting === "true") return;
    if (!this.#validate()) return;
    const items = this.#buildItems();
    if (items.length === 0) return;
    this.dataset.submitting = "true";
    this.#setBusy(true);
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({ items })
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        this.dispatchEvent(
          new ToastEvent({
            message: data.message || Theme.translations.cart_add_failed,
            variant: "error"
          })
        );
        return;
      }
      const messageTemplate = Theme.translations.cart_added;
      const productTitle = this.dataset.productTitle || data.items?.[0]?.product_title || data.product_title || "";
      const successMessage = messageTemplate.includes("__PRODUCT_TITLE__") ? messageTemplate.replace("__PRODUCT_TITLE__", productTitle) : messageTemplate;
      this.dispatchEvent(
        new ToastEvent({ message: successMessage, variant: "success" })
      );
      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: "participant-registration-form",
          itemCount: items.length,
          productId: this.dataset.productId,
          sections: data.sections
        })
      );
      void this.#dialog?.closeDialog?.();
    } catch {
      this.dispatchEvent(
        new ToastEvent({
          message: Theme.translations.cart_add_failed,
          variant: "error"
        })
      );
    } finally {
      this.dataset.submitting = "";
      this.#setBusy(false);
    }
  }
  #buildItems() {
    const variantId = Number(this.dataset.variantId);
    const participants = this.querySelectorAll("details");
    const items = [];
    const teamId = this.#isTeam() ? randomTeamId() : void 0;
    participants.forEach((details, index) => {
      const properties = {};
      details.querySelectorAll("[data-property]").forEach((input) => {
        properties[input.dataset.property ?? ""] = input.value.trim();
      });
      const name = details.querySelector('[data-role="name"]');
      const email = details.querySelector(
        '[data-role="email"]'
      );
      if (name?.value.trim()) properties["_name"] = name.value.trim();
      if (email?.value.trim()) properties["_email"] = email.value.trim();
      properties["_participant"] = String(index + 1);
      if (this.#isTeam()) {
        properties["_team_size"] = String(participants.length);
        if (teamId) properties["_team_id"] = teamId;
      }
      items.push({ id: variantId, quantity: 1, properties });
    });
    return items;
  }
  #validate() {
    let firstInvalid = null;
    for (const details of this.querySelectorAll("details")) {
      for (const input of details.querySelectorAll("input")) {
        if (input.validity.valid) {
          input.removeAttribute("aria-invalid");
        } else {
          input.setAttribute("aria-invalid", "true");
          details.open = true;
          firstInvalid ??= input;
        }
      }
    }
    if (firstInvalid) {
      firstInvalid.focus();
      if (!firstInvalid.validity.valid) firstInvalid.reportValidity();
      return false;
    }
    return true;
  }
  #setBusy(busy) {
    const button = this.querySelector(
      '[ref="submitButton"]'
    );
    if (!button) return;
    button.disabled = busy;
    if (busy) {
      button.dataset.loading = "";
      button.setAttribute("aria-busy", "true");
    } else {
      delete button.dataset.loading;
      button.removeAttribute("aria-busy");
    }
  }
}
if (!customElements.get("participant-registration-form")) {
  customElements.define(
    "participant-registration-form",
    ParticipantRegistrationForm
  );
}
export {
  ParticipantRegistrationForm
};
